import { Component, Input, Output, EventEmitter, ChangeDetectorRef, ChangeDetectionStrategy, ViewChild, ElementRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GeneralService, FileUploadResponse, DynamicTranslationService } from '@gs-2.0/core';
import { MediaPreviewDialogComponent } from '../media-preview-dialog/media-preview-dialog.component';
import { ToastService } from '../../services/toast.service';

export interface GalleryFile {
  fileName: string;
  fileUrl: string;
  fileUuid: string;
  fileSize: string;
  fileType: string;
  hasError: boolean;
  errorMessage: string;
}

export interface GalleryFileUploadedEvent {
  file: GalleryFile;
  apiFileType: string;
}

@Component({
  selector: 'app-gallery-upload-zone',
  standalone: false,
  templateUrl: './gallery-upload-zone.component.html',
  styleUrls: ['./gallery-upload-zone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GalleryUploadZoneComponent {
  @Input() entityUuid = '';
  @Input() fileType = 'PROJECT_IMAGE';
  @Input() videoFileType = '';
  @Input() multiple = true;
  @Input() uploadText = 'settings.project_details_form.gallery_upload_label';
  @Input() uploadingText = 'settings.project_details_form.uploading';
  @Input() uploadFormats = 'settings.project_details_form.gallery_upload_formats';
  @Input() accept = '.jpg,.jpeg,.png,.bmp,.jfif,.webp,.mp4,.mov,image/jpeg,image/jpg,image/png,image/bmp,image/jfif,image/webp,video/mp4,video/quicktime';
  @Input() allowedMimeTypes: string[] = [];
  @Input() set initialFiles(files: GalleryFile[]) {
    if (files && files.length > 0 && this.galleryFiles.length === 0) {
      this.galleryFiles = [...files];
    }
  }

  @Output() filesChange = new EventEmitter<GalleryFile[]>();
  @Output() entityUuidChange = new EventEmitter<string>();
  @Output() fileUploaded = new EventEmitter<GalleryFileUploadedEvent>();

  @ViewChild('fileInput') private fileInputRef!: ElementRef<HTMLInputElement>;

  galleryFiles: GalleryFile[] = [];
  isUploadingGallery = false;

  private readonly defaultAllowedTypes = [
    'image/jpeg', 'image/jpg', 'image/png', 'image/bmp',
    'image/jfif', 'image/webp', 'video/mp4', 'video/quicktime'
  ];

  private get effectiveAllowedTypes(): string[] {
    return this.allowedMimeTypes.length > 0 ? this.allowedMimeTypes : this.defaultAllowedTypes;
  }

  constructor(
    private generalService: GeneralService,
    private dialog: MatDialog,
    private translationService: DynamicTranslationService,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService
  ) {}

  onUploadClick(): void {
    const input = this.getFileInput();
    if (input) input.click();
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files = this.multiple ? Array.from(input.files) : [input.files[0]];

    if (!this.multiple) {
      this.galleryFiles = [];
    }

    const hasExistingVideo = this.galleryFiles.some(f => f.fileType.startsWith('video/'));

    files.forEach(file => {
      if (!this.effectiveAllowedTypes.includes(file.type)) {
        this.galleryFiles.push({
          fileName: file.name,
          fileUrl: '',
          fileUuid: '',
          fileSize: this.formatFileSize(file.size),
          fileType: file.type,
          hasError: true,
          errorMessage: this.translationService.instant('settings.project_details_form.error_invalid_gallery_file_type')
        });
        this.cdr.markForCheck();
        this.filesChange.emit([...this.galleryFiles]);
        return;
      }

      if (file.type.startsWith('video/') && hasExistingVideo) {
        this.toastService.error(this.translationService.instant('settings.project_details_form.error_video_limit_reached'));
        return;
      }

      this.uploadFile(file);
    });
    input.value = '';
  }

  previewFile(index: number): void {
    const file = this.galleryFiles[index];
    if (file?.fileUrl) {
      this.dialog.open(MediaPreviewDialogComponent, {
        data: { url: file.fileUrl, fileName: file.fileName, fileType: file.fileType },
        panelClass: 'media-preview-dialog',
        maxWidth: '100vw',
        maxHeight: '100vh',
        width: '100vw',
        height: '100vh',
      });
    }
  }

  removeFile(index: number): void {
    const file = this.galleryFiles[index];

    if (!file.fileUuid) {
      this.galleryFiles.splice(index, 1);
      this.cdr.markForCheck();
      this.filesChange.emit([...this.galleryFiles]);
      return;
    }

    this.generalService.deleteFile({ entityUuid: this.entityUuid, fileType: this.resolveFileType(file.fileType), fileUuid: file.fileUuid }).subscribe({
      next: () => {
        this.galleryFiles.splice(index, 1);
        this.cdr.markForCheck();
        this.filesChange.emit([...this.galleryFiles]);
      },
      error: () => {}
    });
  }

  private uploadFile(file: File): void {
    const placeholderIndex = this.galleryFiles.length;
    this.galleryFiles.push({
      fileName: file.name,
      fileUrl: '',
      fileUuid: '',
      fileSize: this.formatFileSize(file.size),
      fileType: file.type,
      hasError: false,
      errorMessage: ''
    });
    this.isUploadingGallery = true;
    this.cdr.markForCheck();

    const resolvedApiFileType = this.resolveFileType(file.type);
    this.generalService.uploadFile({ entityUuid: this.entityUuid, fileType: resolvedApiFileType, secondUuid: '', file }).subscribe({
      next: (response: FileUploadResponse) => {
        this.isUploadingGallery = false;
        if (!this.entityUuid && (response as any).objectUuid) {
          this.entityUuid = (response as any).objectUuid;
          this.entityUuidChange.emit(this.entityUuid);
        }
        const rawName = response.response.fileName || file.name;
        const uploadedFile: GalleryFile = {
          fileName: rawName.split('/').pop() || rawName,
          fileUrl: response.response.url,
          fileUuid: response.response.uuid,
          fileSize: this.formatFileSize(file.size),
          fileType: file.type,
          hasError: false,
          errorMessage: ''
        };
        this.galleryFiles[placeholderIndex] = uploadedFile;
        this.cdr.markForCheck();
        this.filesChange.emit([...this.galleryFiles]);
        this.fileUploaded.emit({ file: uploadedFile, apiFileType: resolvedApiFileType });
      },
      error: () => {
        this.isUploadingGallery = false;
        this.galleryFiles[placeholderIndex] = {
          ...this.galleryFiles[placeholderIndex],
          hasError: true,
          errorMessage: this.translationService.instant('settings.project_details_form.error_gallery_upload_failed')
        };
        this.cdr.markForCheck();
        this.filesChange.emit([...this.galleryFiles]);
      }
    });
  }

  private resolveFileType(mimeType: string): string {
    if (this.videoFileType && (mimeType.startsWith('video/') || mimeType === this.videoFileType)) {
      return this.videoFileType;
    }
    return this.fileType;
  }

  private getFileInput(): HTMLInputElement | null {
    return this.fileInputRef?.nativeElement ?? null;
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
